import { ProtectedVideo, MediaError } from "../components/ProtectedMedia";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { api, abs, money, errMsg } from "../lib/api";
import { PageHeader, Loading } from "../components/PageHeader";

function VideoCard({ p, index }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v.paused) return v.pause();
    setFailed(false);
    const pr = v.play();
    if (pr) pr.catch((e) => { if (e?.name !== "AbortError") { setFailed(true); setPlaying(false); } });
  };
  return (
    <div className="group fade-up" style={{ animationDelay: `${index * 90}ms` }} data-testid="video-card">
      <div className="art-frame aspect-video">
        <ProtectedVideo
          ref={ref} mp4={abs(p.preview_url)} webm={abs(p.preview_webm_url)} poster={abs(p.image_urls[0])} preload="none" playsInline
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
          onLoadError={() => { setFailed(true); setPlaying(false); }}
          className="h-full w-full object-cover" data-testid="video-preview-element"
        />
        {failed && <MediaError testid="video-preview-error" />}
        <button
          onClick={toggle} data-testid="video-preview-play-button" aria-label="Play preview"
          className={`absolute inset-0 grid place-items-center transition-opacity duration-300 ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`}
        >
          <span className="grid h-16 w-16 place-items-center rounded-full border border-white/40 bg-black/40 text-white backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
            {playing ? <Pause size={20} /> : <Play size={20} className="ml-1" />}
          </span>
        </button>
        <span className="price-tag absolute right-4 top-4" data-testid="video-card-price">{money(p.price)}</span>
        <span className="absolute left-4 top-4 bg-black/60 px-2 py-1 backdrop-blur-md font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/90">30-sec preview · {p.duration} full</span>
      </div>
      <Link to={`/product/${p.slug}`} className="mt-5 flex items-end justify-between gap-4" data-testid="video-card-link">
        <div>
          <h3 className="font-display text-2xl tracking-tight group-hover:text-wb-gold transition-colors">{p.title}</h3>
          <p className="mt-1 text-xs muted">{p.subtitle}</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] muted">Details →</span>
      </Link>
    </div>
  );
}

export default function Videos() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/products", { params: { type: "video" } }).then((r) => setItems(r.data)).catch((e) => { setItems([]); toast.error(errMsg(e)); });
  }, []);
  return (
    <div data-testid="videos-page">
      <PageHeader eyebrow="Moving images" title="Art films">
        Watch a 30-second preview of each film. Purchase to stream the full piece in your library and download it.
      </PageHeader>
      <div className="wb-container">
        {!items ? <Loading /> : (
          <div className="grid gap-12 py-6 md:grid-cols-2" data-testid="videos-grid">
            {items.map((p, i) => <VideoCard key={p.id} p={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
